import {useCallback, useEffect, useState} from 'react';
import {logger} from '@/helpers/logger';

export const useCsvUploadModal = ({isOpen, existingFileName} = {}) => {
  const [uploadError, setUploadError] = useState('');
  const [parsedData, setParsedData] = useState(null);
  const [isParsingFile, setIsParsingFile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [uploadedFileName, setUploadedFileName] = useState('');
  const [isFileValid, setIsFileValid] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (!isOpen) return;

    setUploadError('');
    setParsedData(null);
    setIsParsingFile(false);
    setIsUploading(false);
    setIsFileValid(false);
    setSelectedFile(null);

    if (existingFileName) {
      setIsUploadComplete(true);
      setUploadedFileName(existingFileName);
    } else {
      setIsUploadComplete(false);
      setUploadedFileName('');
    }
  }, [isOpen, existingFileName]);

  const resetState = useCallback(() => {
    setUploadError('');
    setParsedData(null);
    setIsParsingFile(false);
    setIsUploading(false);
    setIsUploadComplete(false);
    setUploadedFileName('');
    setIsFileValid(false);
    setSelectedFile(null);
  }, []);

  const handleClose = useCallback(
    (onClose) => {
      resetState();
      onClose();
    },
    [resetState]
  );

  const handleFileDelete = useCallback(() => {
    resetState();
  }, [resetState]);

  const handleDone = useCallback(
    (onDone, onClose) => {
      if (!isUploadComplete) return;
      if (parsedData) {
        onDone();
      }
      handleClose(onClose);
    },
    [isUploadComplete, parsedData, handleClose]
  );

  const validateFile = useCallback(async (file, parseFile, convertFile) => {
    try {
      setUploadError('');
      setParsedData(null);
      setIsParsingFile(true);
      setIsUploading(false);
      setIsUploadComplete(false);
      setUploadedFileName('');
      setIsFileValid(false);
      setSelectedFile(file);

      const fileToParse = convertFile ? await convertFile(file) : file;
      if (convertFile) {
        setSelectedFile(fileToParse);
      }

      const validatedData = await parseFile(fileToParse);
      setParsedData(validatedData);
      setIsFileValid(true);
      setUploadedFileName(file.name);
    } catch (error) {
      logger.error('CSV validation error:', error);
      setUploadError(error.message || 'Error parsing CSV');
      setParsedData(null);
      setIsFileValid(false);
      setUploadedFileName('');
      setSelectedFile(null);
    } finally {
      setIsParsingFile(false);
    }
  }, []);

  const uploadFile = useCallback(
    async (onUpload) => {
      if (!selectedFile || !parsedData || !isFileValid) return;

      try {
        setIsUploading(true);
        setUploadError('');

        const didUploadSucceed = await onUpload(selectedFile, parsedData);
        if (didUploadSucceed === false) {
          throw new Error('Failed to upload file. Please try again.');
        }

        setIsUploadComplete(true);
        setUploadError('');
      } catch (error) {
        logger.error('Blob upload error:', error);
        setUploadError(
          error.message || 'Failed to upload file. Please try again.'
        );
        setIsUploadComplete(false);
      } finally {
        setIsUploading(false);
      }
    },
    [selectedFile, parsedData, isFileValid]
  );

  const processFile = useCallback(
    async (file, {preValidate, parseFile, onUpload, convertFile}) => {
      try {
        const preError = preValidate(file);
        if (preError) {
          setParsedData(null);
          setIsParsingFile(false);
          setIsUploading(false);
          setIsUploadComplete(false);
          setUploadedFileName('');
          setIsFileValid(false);
          setSelectedFile(null);
          setUploadError(preError);
          return;
        }

        setUploadError('');
        setParsedData(null);
        setIsParsingFile(true);
        setIsUploading(false);
        setIsUploadComplete(false);
        setUploadedFileName('');
        setIsFileValid(false);
        setSelectedFile(file);

        const fileToParse = convertFile ? await convertFile(file) : file;
        if (convertFile) {
          setSelectedFile(fileToParse);
        }

        const validatedData = await parseFile(fileToParse);
        setParsedData(validatedData);

        setIsUploading(true);
        const didUploadSucceed = await onUpload(fileToParse, validatedData);
        if (didUploadSucceed === false) {
          throw new Error('Failed to upload file. Please try again.');
        }

        setIsUploadComplete(true);
        setUploadedFileName(file.name);
        setUploadError('');
        setIsFileValid(true);
        setIsParsingFile(false);
      } catch (error) {
        logger.error('CSV processing error:', error);
        setUploadError(error.message || 'Error parsing CSV');
        setParsedData(null);
        setIsUploadComplete(false);
        setUploadedFileName('');
        setIsFileValid(false);
        setSelectedFile(null);
        setIsParsingFile(false);
      } finally {
        setIsUploading(false);
      }
    },
    []
  );

  return {
    uploadError,
    parsedData,
    isParsingFile,
    isUploading,
    isUploadComplete,
    uploadedFileName,
    isFileValid,
    selectedFile,
    resetState,
    handleClose,
    handleFileDelete,
    handleDone,
    processFile,
    validateFile,
    uploadFile
  };
};
